import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import palette from '@ui/palette';
import usePhonetic from '@replacements/hooks/usePhonetic';
import Playback from '@replacements/components/PhoneticLibrary/Playback';

interface DictionarySuggestionPlaybackProps {
  word: string;
  replacement: string;
  location: string;
  onAudioPlayback: (state: AudioState) => void;
  isRespelling?: boolean;
}

const useStyles = makeStyles()(theme => ({
  listPadding: {
    padding: 0,
  },
  playback: {
    padding: theme.spacing(0.5),
    borderRadius: theme.spacing(0.5),
    '&:focus-visible': {
      outline: `2px solid ${palette.mint_200}`,
    },
  },
  loading: {
    minHeight: theme.spacing(3),
  },
}));

/**
 * Helps represent the state with the goal of
 * knowing when the audio has been requested
 * (play button clicked by user for first time)
 * vs when the audio is requested, loaded, and has finished playing (done state)
 */
export enum AudioState {
  ready = 0,
  requested = 1,
  playing = 2,
}

const DictionarySuggestionPlayback = (
  props: DictionarySuggestionPlaybackProps
) => {
  const {
    word,
    replacement: replacementText,
    onAudioPlayback,
    location,
    isRespelling,
  } = props;

  const { preview, replacement } = usePhonetic({
    onSuccess: phoneticReplacement => {},
  });

  useEffect(() => {
    replacement.onTextChange(replacementText);
    preview.onTextChange(word);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * True if audio playback is loading for the first time
   */
  const [playbackLoading, setPlaybackLoading] = useState(false);

  /**
   * True if the audio is being played back to user
   */
  const [audioPlaying, setAudioPlaying] = useState(false);
  const { classes } = useStyles();

  /**
   * True if audio has loaded for the first (only) time
   */
  const [audioLoaded, setAudioLoaded] = useState(false);
  const [audioState, setAudioState] = useState(AudioState.ready);

  useEffect(() => {
    if (playbackLoading) {
      setAudioState(AudioState.requested);
      setAudioLoaded(true);
    }

    /**
     * Requested state is only set once while we wait for audio to initially load
     * If it's already loaded, we can use `audioLoaded` flag to check if it's playing
     */
    if ((audioState === AudioState.requested || audioLoaded) && audioPlaying)
      setAudioState(AudioState.playing);

    /**
     * If audio is playing and finishes, reset the state back to ready
     * At this point state will either be ready or playing
     */
    if (audioState === AudioState.playing && !audioPlaying)
      setAudioState(AudioState.ready);
  }, [audioLoaded, audioPlaying, audioState, playbackLoading]);

  useEffect(() => {
    onAudioPlayback(audioState);
  }, [audioState, onAudioPlayback]);

  return (
    <Box display={'flex'} alignItems={'center'} minHeight={24}>
      <Playback
        label="preview input pronunciation"
        className={classes.playback}
        preview={replacement}
        original={preview}
        isRespelling={isRespelling ?? true}
        onLoading={setPlaybackLoading}
        setAudioPlaying={setAudioPlaying}
        // todo verify naming see @ trackPhoneticLibraryPreviewRender
        location={location}
      />
    </Box>
  );
};

export default DictionarySuggestionPlayback;
